import * as _ from 'lodash'
import * as React from 'react'
import * as Material from 'react-materialize'
import { Alert, Button, Col, FormGroup, Row } from 'reactstrap'
import { GridContainer } from '../../components/ui/Grid'
import {Wizard} from '../../components/ui/Wizard';
import { HttpRequest } from '../../util/http';
import { userData } from '../../util/userData'
import { Container } from '../Container'
import { HierarchyJsTree } from './components/HierarchyJsTree'
import { PairHierarchysAndSurveys } from './components/PairHierarchysAndSurveys';
import { PairSegmentsAndSurveys } from './components/PairSegmentsAndSurveys';
import { SegmentJsTree } from './components/SegmentJsTree';
import { SurveyPicker } from './components/SurveyPicker';
import { NewProjectHttp } from './http';
import { ICustomerType, IProject } from './interfaces';

interface INewProjectContainerState {
  hierarchyProject: string
  segmentProject: string
  completed: boolean
  error: any
  projects: IProject[]
  customerTypes: ICustomerType[]
  newProject: IProject
}

export class NewProjectContainer extends Container<{}, INewProjectContainerState> {
  state: INewProjectContainerState = {
    hierarchyProject: '',
    segmentProject: '',
    projects: [],
    customerTypes: [],
    completed: false,
    error: undefined,
    newProject: {
      projectName: '',
      abbreviation: '',
      domain: '',
      customerType: 'Bank',
      allSurveysType: 'All',
      dynamicAgeInDays: undefined,
      surveyReferences: [],
      hierarchyReferences: [],
      segmentReferences: [],
      hierarchyStudies: [],
      segmentHierarchyStudies: [],
      customerTypeId: 1,
    }
  }

  wizardElement: HTMLElement

  componentWillMount() {
    this.loadProjects()
    this.loadCustomerTypes()
  }

  componentDidMount() {
    if (this.wizardElement) {
      const inputs = this.wizardElement.getElementsByTagName('input')
      if (inputs[0]) {
        inputs[0].focus()
      }
    }
  }

  loadCustomerTypes() {
    NewProjectHttp.getCustomerTypes().then((customerTypes) => {
      const customerType = customerTypes[0].name
      const customerTypeId = customerTypes[0].id

      this.updateNewProject('customerType', customerType)
      this.updateNewProject('customerTypeId', customerTypeId)

      this.setState({ customerTypes })
    })
  }

  loadProjects() {
    return NewProjectHttp.getProjects().then((projects) => {
      let hierarchyProject = projects[0].abbreviation
      const currentProject = userData.getCurrentProject()
      const defaultProject = projects.find((p) => p.abbreviation === currentProject)

      if (defaultProject) {
        hierarchyProject = defaultProject.abbreviation
      }

      const segmentProject = hierarchyProject

      this.setState({ projects, hierarchyProject, segmentProject })

      return projects
    })
  }

  updateNewProject<K extends keyof IProject>(key: K, value: IProject[K]) {
    const { newProject } = this.state

    newProject[key] = value

    if (key === 'customerType') {
      const customerType = this.state.customerTypes.find((ct) => ct.name === value)

      if (customerType) {
        newProject.customerTypeId = customerType.id
      }
    }

    this.setState({ newProject })
  }

  onComplete() {
    const { newProject } = this.state

    return NewProjectHttp.saveProject(newProject)
      .catch((error) => this.setState({ error }))
      .finally(() => this.setState({ completed: true }))
  }

  redirect(url, client) {
    HttpRequest.fetch('../scripts/clients/change_project', { method: 'POST', params: { project: client }, body: JSON.stringify({ project: client }) })
      .then(() => {
        // @ts-ignore
        userData.storeUserDataInSession('current_project', client);
        window.location = url;
      })
      // tslint:disable-next-line:no-console
      .catch((err) => { console.log(err) })
  }

  render() {
    const { newProject, completed, error } = this.state
    const allSurveyOptions = NewProjectHttp.getAllSurveysCombinedOptions()

    return (
      <GridContainer className="blacklight">
        <header>
          <h1 className="mt-4 mb-4">
            <span>New Project&nbsp;</span>
            <small className="text-muted">{newProject.projectName}</small>
          </h1>
        </header>
        <section className="new-project-container">
          <section className="new-project-container-content">
            {error && completed &&
              <Alert color="danger">
                <h3>
                  <i className="fas fa-fw fa-exclamation-triangle" />&nbsp;&nbsp;
                  <span>Uh oh! An unexpected error occured.</span>
                </h3>
                <section style={{ overflowWrap: 'break-word' }}>
                  Error: {JSON.stringify(error)}
                </section>
              </Alert>
            }
            {!error && completed &&
              <Alert color="success">
                <h3>
                  <i className="fas fa-fw fa-check-circle" />&nbsp;
                  <span>{newProject.projectName} has been successfully created!</span>
                </h3>
                <Button style={{ marginLeft: '5px' }} className="btn-primary" onClick={() => this.redirect('clients', newProject.abbreviation)} >Go to Client List</Button>
                <Button style={{ marginLeft: '5px' }} className="btn-primary" onClick={() => this.redirect('surveys', newProject.abbreviation)} >Manage Surveys</Button>
                <Button style={{ marginLeft: '5px' }} className="btn-primary" onClick={() => this.redirect('hierarchies', newProject.abbreviation)} >Manage Avids, Hierarchies, and Segments</Button>
                {/* <Button style={{ marginLeft: '5px' }} className="btn-primary" onClick={() => this.redirect('manage_segments', newProject.abbreviation)} >Manage Segments</Button> */}
              </Alert>
            }
            {!error && !completed &&
              <Wizard
                autoComplete="off"
                onComplete={() => this.onComplete()}
                formRef={(wizardElement) => this.wizardElement = wizardElement}>
                <Wizard.Tab header="1. Client Information" className="p-4 subtleSlideInRight">
                  <Row>
                    <Col lg={6}>
                      <FormGroup>
                        <Material.Input label="Client Name"
                          onChange={(e) => this.updateNewProject('projectName', e.currentTarget.value)}
                          value={newProject.projectName}
                          required />
                      </FormGroup>
                      <FormGroup>
                        <Material.Input label="Abbreviation"
                          onChange={(e) => this.updateNewProject('abbreviation', e.currentTarget.value)}
                          value={newProject.abbreviation}
                          required />
                      </FormGroup>
                      <FormGroup>
                        <Material.Input label="Domain"
                          onChange={(e) => this.updateNewProject('domain', e.currentTarget.value)}
                          value={newProject.domain}
                          required />
                      </FormGroup>
                      <FormGroup>
                        <Material.Input type="select" label="Customer Type"
                          onChange={(e) => this.updateNewProject('customerType', e.currentTarget.value)}
                          value={newProject.customerType}>
                          {this.state.customerTypes.map((customerType) =>
                            <option key={customerType.id} value={customerType.name}>{customerType.name}</option>
                          )}
                        </Material.Input>
                      </FormGroup>
                      <FormGroup>
                        <Material.Input label="Dynamic Age in Days" type="number"
                          onChange={(e) => this.updateNewProject('dynamicAgeInDays', e.currentTarget.value)}
                          value={newProject.dynamicAgeInDays}
                          required />
                      </FormGroup>
                    </Col>
                  </Row>
                </Wizard.Tab>
                <Wizard.Tab header="2. Define Surveys" className="p-4 fadeIn">
                  <SurveyPicker
                    values={newProject.surveyReferences}
                    onChange={(surveyReferences) => this.updateNewProject('surveyReferences', surveyReferences)} />

                  <section className="mt-3 mb-3">
                    <label>All Surveys Combined Survey</label>
                    {allSurveyOptions.map((allSurveyOption) =>
                      <article>
                        <input id={allSurveyOption.value} name="group1" type="radio" required
                          value={allSurveyOption.value}
                          checked={newProject.allSurveysType === allSurveyOption.value}
                          onChange={() => this.updateNewProject('allSurveysType', allSurveyOption.value)} />
                        <label htmlFor={allSurveyOption.value}>{allSurveyOption.display}</label>
                      </article>
                    )}
                  </section>
                </Wizard.Tab>
                <Wizard.Tab header="3. Define Hierarchys" className="p-4 subtleSlideInRight">
                  <Row>
                    <Col lg={6}>
                      <Material.Input type="select" label="Client"
                        onChange={(e) => this.setState({ hierarchyProject: e.currentTarget.value })}
                        value={this.state.hierarchyProject}>
                        {this.state.projects.map((p) =>
                          <option key={p.abbreviation} value={p.abbreviation}>{p.projectName}</option>
                        )}
                      </Material.Input>

                      <article>
                        <header>
                          <label>Select the nodes you would like to use as hierarchys:</label>
                        </header>
                        <section>
                          <HierarchyJsTree
                            values={newProject.hierarchyReferences}
                            onChange={(hierarchyReferences) => this.updateNewProject('hierarchyReferences', hierarchyReferences)}
                            project={this.state.hierarchyProject}
                            required />
                        </section>
                      </article>
                    </Col>
                  </Row>
                </Wizard.Tab>
                <Wizard.Tab header="4. Pair Hierarchys and Surveys" className="data-list-tab-pane fadeIn">
                  <PairHierarchysAndSurveys
                    project={this.state.hierarchyProject}
                    selectedSurveys={newProject.surveyReferences}
                    selectedHierarchys={newProject.hierarchyReferences}
                    values={newProject.hierarchyStudies}
                    onChange={(hierarchyStudies) => this.updateNewProject('hierarchyStudies', hierarchyStudies)} />
                </Wizard.Tab>
                <Wizard.Tab header="5. Defined Segments" className="p-4 subtleSlideInRight">
                  <Row>
                    <Col lg={6}>
                      <Material.Input type="select" label="Client"
                        onChange={(e) => this.setState({ segmentProject: e.currentTarget.value })}
                        value={this.state.segmentProject}>
                        {this.state.projects.map((p) =>
                          <option key={p.abbreviation} value={p.abbreviation}>{p.projectName}</option>
                        )}
                      </Material.Input>

                      <article>
                        <label>Select the nodes you would like to use as segments:</label>
                        <SegmentJsTree
                          values={newProject.segmentReferences}
                          onChange={(segmentReferences) => this.updateNewProject('segmentReferences', segmentReferences)}
                          project={this.state.segmentProject}
                        />
                      </article>
                    </Col>
                  </Row>
                </Wizard.Tab>
                <Wizard.Tab header="6. Pair Segments and Surveys" className="data-list-tab-pane fadeIn">
                  <PairSegmentsAndSurveys
                    project={this.state.segmentProject}
                    selectedSurveys={newProject.surveyReferences}
                    selectedSegments={newProject.segmentReferences}
                    values={newProject.segmentHierarchyStudies}
                    onChange={(segmentHierarchyStudies) => this.updateNewProject('segmentHierarchyStudies', segmentHierarchyStudies)} />
                </Wizard.Tab>
              </Wizard>
            }
          </section>
        </section>
      </GridContainer >
    )
  }
}
